import React, { useEffect, useState } from 'react';
import { Badge, Box, Button, Drawer } from '@mui/material';
import { FilterList as FilterIcon } from '@mui/icons-material';
import { useViewport } from '../../../shared/hooks/useViewport';
import type { FacetFilterState } from '../../../shared/components/FacetedFilterRail';

/** Rail width beside a table. */
const RAIL_WIDTH = 240;

/** Drawer width from `sm` up; a phone gets most of its screen width. */
const DRAWER_WIDTH = 360;

interface FilterRailSlotProps {
  /** The rail's state, read only to count the filters in force for the Filters button. */
  state: FacetFilterState;
  /** The filter rail itself. */
  children: React.ReactNode;
}

const activeFilterCount = (state: FacetFilterState): number =>
  Object.values(state.selections).reduce((n, values) => n + values.length, 0) + (state.search ? 1 : 0);

/**
 * Where a filter rail sits beside a table. Below `lg` the width goes to the table and its
 * detail, so the rail waits in a drawer behind a Filters button that counts the filters in
 * force; from `lg` up it stays beside the table.
 * The rail lives inside full-screen dialogs, so the drawer takes the modal layer: at the
 * drawer layer it would open behind the dialog that holds it.
 */
const FilterRailSlot: React.FC<FilterRailSlotProps> = ({ state, children }) => {
  const inDrawer = !useViewport().isDesktop;
  const [open, setOpen] = useState(false);

  // Widening past the drawer unmounts it with the rail moved inline; forget it was open,
  // so narrowing again does not reopen it unasked.
  useEffect(() => {
    if (!inDrawer) setOpen(false);
  }, [inDrawer]);

  if (!inDrawer) {
    return <Box sx={{ width: RAIL_WIDTH, flexShrink: 0 }}>{children}</Box>;
  }

  const count = activeFilterCount(state);
  return (
    <Box sx={{ px: 1, py: 0.5, borderBottom: 1, borderColor: 'divider', flexShrink: 0 }}>
      <Button
        size="small"
        onClick={() => setOpen(true)}
        aria-label={count > 0 ? `Filters, ${count} active` : 'Filters'}
        startIcon={
          <Badge badgeContent={count} color="primary">
            <FilterIcon fontSize="small" />
          </Badge>
        }
      >
        Filters
      </Button>
      <Drawer
        anchor="left"
        open={open}
        onClose={() => setOpen(false)}
        sx={{ zIndex: (theme) => theme.zIndex.modal }}
        PaperProps={{ sx: { width: { xs: '85vw', sm: DRAWER_WIDTH } } }}
      >
        {children}
      </Drawer>
    </Box>
  );
};

export default FilterRailSlot;
